/**
 * Every Market Intelligence URL is built here.
 *
 * Mirrors `lib/record-paths.ts`. The previous scheme in this repo hardcoded
 * paths across ~41 files, which made one re-path a 41-file change; nobody
 * hand-writes an MI url.
 *
 * **Every url names its commodity first.** Electric and gas are separate
 * markets with separate topologies, so the url says which one you are in
 * before it says anything else — and then the two read the same:
 *
 *     /market-intelligence/electric/regions/caiso/hubs/caiso_sp15/forwards
 *     /market-intelligence/gas/regions/northeast/hubs/algonquin_cg/forwards
 *
 * **Hubs nest under their region**, in both commodities. A point belongs to
 * exactly one region, so the url says so. The flat `/hubs/:hub` form came
 * first and was wrong in a way that showed: the region had to be inferred from
 * the hub to keep the sidebar correct. Nesting makes the hierarchy structural —
 * the breadcrumb, the sidebar scope and the url all read the same thing off
 * the route. Gas kept a flat `/gas/hubs/:hub` form until it had regions
 * (2026-10-02).
 *
 * **`electric` is a URL word, not a data value.** The commodity persisted in
 * `market.strip_rolling.commodity` (and CHECKed there) is `power`, and that
 * has NOT changed — renaming a database value to match a label would be a
 * migration across every market table for a cosmetic gain. `MarketCommodity`
 * stays `"power" | "gas"`; only what the user reads and types changes. See
 * `COMMODITY_SLUG` below for the one place the two vocabularies meet.
 *
 * The ids are the mock topology's codes today (`caiso`, `caiso_sp15`). They
 * are stable slugs chosen to survive the swap to real data: the region slug is
 * the ISO code lowercased, and the hub id is `market.pricing_zones.code` where
 * one exists.
 */

import type { MarketCommodity } from "@/types/market-intelligence";

export const MI_PATH = "/market-intelligence";

/**
 * The url segment for each commodity.
 *
 * The single bridge between the DATA vocabulary (`power`, as stored and
 * CHECKed in every `market.*` table) and the URL/label vocabulary
 * (`electric`, what the product calls it). Everything else on either side
 * uses its own word without translating, because this map is the only place
 * that has to know both.
 */
export const COMMODITY_SLUG: Record<MarketCommodity, string> = {
  power: "electric",
  gas: "gas",
};

/** Electric's commodity root. */
export const MI_ELECTRIC_PATH = `${MI_PATH}/${COMMODITY_SLUG.power}`;

/** The sidebar parent group's storage key. NOT a route the router serves. */
export const MI_REGIONS_GROUP_PATH = `${MI_ELECTRIC_PATH}/regions`;

/**
 * Gas's commodity root, and gas's Overview. Everything under it is on gas's
 * side of the toggle.
 *
 * The gas Overview used to be electric's url with `?commodity=gas`, so every
 * link back to "the Overview" — the sidebar row, the breadcrumb — was a link
 * to electric's, and dropped a gas reader out of gas. A path of its own is what
 * lets each of those links say which commodity it means (`overviewPath`).
 */
export const MI_GAS_PATH = `${MI_PATH}/${COMMODITY_SLUG.gas}`;

/**
 * A commodity's Overview: where "back to Market Intelligence" goes from
 * anywhere in that commodity. Electric's is the module root.
 */
export function overviewPath(commodity: MarketCommodity): string {
  return commodity === "gas" ? MI_GAS_PATH : MI_PATH;
}

/** Gas's counterpart to `MI_REGIONS_GROUP_PATH`: the region picker's base. */
export const MI_GAS_REGIONS_GROUP_PATH = `${MI_GAS_PATH}/regions`;

export type HubTab = "forwards" | "index" | "capacity";
/**
 * A region's surfaces. `hubs`, `load-zones` and `delivery-points` are the
 * region overview filtered to one kind of point; the bare region url is the
 * same overview, unfiltered. Load zones are power's, delivery points gas's.
 */
export type RegionTab = "hubs" | "load-zones" | "delivery-points" | "recs" | "capacity";

/**
 * A region code → its url slug: lower-case, every run of anything else one
 * hyphen. "ISO-NE" → "iso-ne", "Louisiana/Southeast" → "louisiana-southeast". The server's
 * region endpoint reduces codes the same way, so it answers either spelling.
 */
export function regionSlug(code: string): string {
  return code
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** ISO code → url slug. "ISO-NE" → "iso-ne". */
export function isoSlug(iso: string): string {
  return regionSlug(iso);
}

export function regionPath(iso: string, tab?: RegionTab): string {
  return `${MI_REGIONS_GROUP_PATH}/${isoSlug(iso)}${tab ? `/${tab}` : ""}`;
}

/** A gas region, by its code ("Upper Midwest" → `/gas/regions/upper-midwest`). */
export function gasRegionPath(region: string, tab?: RegionTab): string {
  return `${MI_GAS_REGIONS_GROUP_PATH}/${regionSlug(region)}${tab ? `/${tab}` : ""}`;
}

/** Either commodity's region, for the surfaces both share. */
export function marketRegionPath(
  commodity: MarketCommodity,
  region: string,
  tab?: RegionTab,
): string {
  return commodity === "gas" ? gasRegionPath(region, tab) : regionPath(region, tab);
}

/**
 * A hub, nested under the region that contains it.
 *
 * `iso` is required rather than derived from the hub: the caller always knows
 * it, and looking it up here would put a topology dependency in a path module
 * that is otherwise pure string-building.
 */
export function hubPath(iso: string, hub: string, tab?: HubTab): string {
  return `${regionPath(iso)}/hubs/${hub}${tab ? `/${tab}` : ""}`;
}

/**
 * A gas point, nested under its region — `hubPath`'s gas counterpart. Kept as
 * its own function rather than an overload so a caller can't build a gas url
 * by passing an ISO code that happens to resolve.
 */
export function gasHubPath(region: string, hub: string, tab?: HubTab): string {
  return `${gasRegionPath(region)}/hubs/${hub}${tab ? `/${tab}` : ""}`;
}

/**
 * Any pricing point's page, from the point itself: `iso` is its region in
 * either commodity, so one call serves a card, a row or a link that may hold
 * either. `commodity` is a plain string so a wire `ZoneRef` passes as it
 * arrives; anything but `gas` is power, the same reading the server gives it.
 */
export function pointPath(
  point: { commodity: MarketCommodity | (string & {}); iso: string; code: string },
  tab?: HubTab,
): string {
  return point.commodity === "gas"
    ? gasHubPath(point.iso, point.code, tab)
    : hubPath(point.iso, point.code, tab);
}
